import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { capturePostHogEvent } from '../../utils/analytics';
import { createCheckoutAttemptId } from '../../utils/checkoutAttemptId';
import {
  createHostedCheckoutSession,
  HOSTED_CHECKOUT_FEATURE_FLAG,
  type HostedCheckoutItem,
} from '../../utils/hostedCheckout';

type Props = {
  items: HostedCheckoutItem[];
  promoCode?: string | null;
  returnContext: 'subscriptions' | 'signup_survey' | 'ai_agent';
  sourceRoute: string;
  flowContext?: string | null;
  surface: string;
  assignment: {
    assignmentId: string;
    subjectId: string;
    exposedAt: string;
  };
  getApiToken?: (() => Promise<string | null>) | null;
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8082';

export default function HostedCheckoutLauncher({
  items,
  promoCode,
  returnContext,
  sourceRoute,
  flowContext,
  surface,
  assignment,
  getApiToken,
}: Props) {
  const attemptIdRef = useRef<string | null>(null);
  if (!attemptIdRef.current) attemptIdRef.current = createCheckoutAttemptId('hosted_checkout');
  const launchedRef = useRef(false);
  const [attemptNumber, setAttemptNumber] = useState(0);
  const [error, setError] = useState('');

  const launch = useCallback(async () => {
    if (launchedRef.current) return;
    launchedRef.current = true;
    setError('');
    const checkoutAttemptId = attemptIdRef.current;
    if (!checkoutAttemptId) return;
    const eventProps = {
      checkout_attempt_id: checkoutAttemptId,
      checkout_experience_assignment_id: assignment.assignmentId,
      checkout_experience_subject_id: assignment.subjectId,
      checkout_experience_surface: surface,
      checkout_experience_variant: 'stripe_hosted',
      checkout_experience_feature_flag: HOSTED_CHECKOUT_FEATURE_FLAG,
      source: sourceRoute,
    };
    capturePostHogEvent('checkout_experience_session_requested', eventProps);
    try {
      const token = getApiToken ? await getApiToken().catch(() => null) : null;
      const result = await createHostedCheckoutSession({
        apiUrl: API_URL,
        token,
        checkoutAttemptId,
        items,
        promoCode,
        returnContext,
        attribution: {
          sourceRoute,
          flowContext,
          experimentKey: HOSTED_CHECKOUT_FEATURE_FLAG,
          variant: 'stripe_hosted',
          assignmentId: assignment.assignmentId,
          subjectId: assignment.subjectId,
          surface,
          exposedAt: assignment.exposedAt,
        },
      });
      capturePostHogEvent('checkout_experience_session_created', {
        ...eventProps,
        stripe_checkout_session_id: result.sessionId,
      });
      capturePostHogEvent('checkout_experience_redirected', eventProps);
      window.location.assign(result.checkoutUrl);
    } catch (launchError) {
      launchedRef.current = false;
      const message =
        launchError instanceof Error
          ? launchError.message
          : 'Secure checkout could not be opened. Please try again.';
      setError(message);
      capturePostHogEvent('checkout_experience_launch_failed', {
        ...eventProps,
        error_message: message,
      });
    }
  }, [assignment, flowContext, getApiToken, items, promoCode, returnContext, sourceRoute, surface]);

  useEffect(() => {
    void launch();
  }, [attemptNumber, launch]);

  return (
    <main className="checkout-page" aria-live="polite">
      <div className="checkout-loading">
        {error ? (
          <>
            <h1>Secure checkout could not open</h1>
            <p role="alert">{error}</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                attemptIdRef.current = createCheckoutAttemptId('hosted_checkout');
                setAttemptNumber((value) => value + 1);
              }}
            >
              Try again
            </button>
          </>
        ) : (
          <>
            <Spinner animation="border" />
            <span>Opening secure Stripe checkout…</span>
          </>
        )}
      </div>
    </main>
  );
}
